import React, { useMemo } from 'react'
import { Package } from 'lucide-react'
import Card from '../Card'
import DataTable, { Column } from '../DataTable'

interface TopProduct {
  id: string
  name: string
  image?: string
  unitsSold: number
  revenue: number
  stock: number
}

interface TopProductsTableProps {
  products: TopProduct[]
  loading?: boolean
}

export default function TopProductsTable({ products, loading = false }: TopProductsTableProps) {
  const columns: Column<TopProduct>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Product',
        render: (value, row) => (
          <div className="flex items-center gap-3">
            {row.image ? (
              <img src={row.image} alt={value} className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                <Package size={16} className="text-gray-400" />
              </div>
            )}
            <span>{value}</span>
          </div>
        ),
        sortable: true,
        sortType: 'text',
      },
      {
        key: 'unitsSold',
        header: 'Units Sold',
        sortable: true,
        sortType: 'number',
      },
      {
        key: 'revenue',
        header: 'Revenue',
        render: (value) => `₹${Number(value).toFixed(2)}`,
        sortable: true,
        sortType: 'price',
      },
      {
        key: 'stock',
        header: 'Stock',
        sortable: true,
        sortType: 'number',
      },
    ],
    []
  )

  if (loading) {
    return (
      <Card title="Top Selling Products" subtitle="Your best performing products">
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-lg"></div>
          ))}
        </div>
      </Card>
    )
  }

  if (!products || products.length === 0) {
    return (
      <Card title="Top Selling Products" subtitle="Your best performing products">
        <div className="py-12 flex flex-col items-center justify-center text-gray-500">
          <Package size={40} className="mb-2 opacity-50" />
          <p>No product sales data available</p>
        </div>
      </Card>
    )
  }

  return (
    <Card title="Top Selling Products" subtitle="Your best performing products">
      <DataTable<TopProduct>
        columns={columns}
        data={products}
      />
      <button className="mt-4 w-full py-2 text-center text-[#6D3A0D] font-medium hover:bg-gray-50 rounded-lg transition-colors">
        View All Products
      </button>
    </Card>
  )
}
